import { Editor, Transforms, Path } from 'slate';
import { CustomText } from '../utils/element-renderer';

// Shortlinks are inline elements that represent tracked URLs in SMS messages.
// They store both the shortened URL and the original URL with its tracking parameters
// to allow for later editing.
export interface ShortLinkElement {
  type: 'shortlink';
  shortUrl: string; // The shortened URL that will be sent in the SMS
  originalData: {
    baseUrl: string; // The original URL before shortening
    parameters: {
      // Tracking parameters that were added
      name: string;
      value: string;
    }[];
  };
  children: CustomText[];
}

// Creates and inserts a new shortlink at the current selection.
// Shortlinks are always void elements (no editable content).
export const insertShortLink = (
  editor: Editor,
  shortUrl: string,
  originalData: ShortLinkElement['originalData']
) => {
  const shortlink: ShortLinkElement = {
    type: 'shortlink',
    shortUrl,
    originalData,
    children: [{ text: '' }]
  };

  Transforms.insertNodes(editor, shortlink);
};

// Updates an existing shortlink's URL and tracking data.
// Used when editing a shortlink's parameters or regenerating the short URL.
export const updateShortLink = (
  editor: Editor,
  path: Path,
  shortUrl: string,
  originalData: ShortLinkElement['originalData']
) => {
  Transforms.setNodes<ShortLinkElement>(
    editor,
    {
      shortUrl,
      originalData
    },
    { at: path }
  );
};

// Slate plugin that customizes editor behavior for shortlinks.
// Key features:
// 1. Shortlinks are inline elements (flow with text)
// 2. Shortlinks are void (no editable content)
export const withShortLinks = (editor: Editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) => {
    return element.type === 'shortlink' ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === 'shortlink' ? true : isVoid(element);
  };

  return editor;
};
